import React, { useState, useEffect, useContext } from 'react';
import { DashboardProvider, DashboardContext } from '../../context/DashboardContext';
import AdminLayout from '../../components/Admin/Layout/AdminLayout';
import DateRangeFilter from '../../components/Admin/Dashboard/DateRangeFilter';
import HeroCards from '../../components/Admin/Dashboard/HeroCards';
import RevenueChart from '../../components/Admin/Dashboard/RevenueChart';
import TopSellingItems from '../../components/Admin/Dashboard/TopSellingItems';
import BankCollections from '../../components/Admin/Dashboard/BankCollections';
import FinancialSnapshot from '../../components/Admin/Dashboard/FinancialSnapshot';
import { useAuth } from '../../context/AuthContext';
import branchService from '../../services/branchService';
import { Building2, ChevronDown } from 'lucide-react';
import { P } from '../../config/permissionCodes';

function BranchFilter() {
  const { user } = useAuth();
  const { selectedBranchId, setSelectedBranchId } = useContext(DashboardContext);
  const isOwner = user?.userType === 'SUPER_USER';
  const [branches, setBranches] = useState([]);

  useEffect(() => {
    if (!isOwner) return;
    branchService.getAllBranches().then(result => {
      if (result && result.length > 1) setBranches(result);
    }).catch(console.error);
  }, [isOwner]);

  if (!branches.length || branches.length <= 1) return null;

  return (
    <div className="relative">
      <Building2 size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
      <select
        value={selectedBranchId ?? ''}
        onChange={(e) => setSelectedBranchId(e.target.value || null)}
        className="pl-9 pr-8 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-teal-500 focus:border-teal-500 appearance-none cursor-pointer"
      >
        <option value="">All Branches</option>
        {branches.map(b => (
          <option key={b.id} value={b.id}>{b.name}</option>
        ))}
      </select>
      <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
    </div>
  );
}

function DashboardContent() {
  const { hasPermission } = useAuth();

  const canView = hasPermission(P.BACK_OFFICE.DASHBOARD.SCREEN);

  // Access denied screen — same pattern as CampaignsPage
  if (!canView) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-full text-center px-6 py-20">
          <div>
            <p className="text-lg font-semibold text-gray-700">Access Denied</p>
            <p className="text-sm text-gray-500 mt-1">You don't have permission to view the dashboard.</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="bg-neutral-50 min-h-full py-6">
        <div className="max-w-8xl mx-auto space-y-6 px-4 sm:px-6 lg:px-8">

          {/* Filter Section */}
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex-1 min-w-0">
              <DateRangeFilter />
            </div>
            <BranchFilter />
          </div>

          {/* Hero Cards */}
          <HeroCards />

          {/* Revenue Chart + Financial Snapshot + Bank Collections */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2" style={{ minHeight: '400px' }}>
              <RevenueChart />
            </div>
            <div className="lg:col-span-1 space-y-6">
              <FinancialSnapshot />
              <BankCollections />
            </div>
          </div>

          {/* Top Selling Items */}
          <div className="bg-white p-6 rounded-2xl shadow-xl border border-gray-100 transition duration-300 hover:shadow-2xl">
            <h2 className="text-xl font-bold text-slate-800 mb-4 border-b pb-2">Top Selling Items</h2>
            <TopSellingItems />
          </div>

          <div className="h-8" />
        </div>
      </div>
    </AdminLayout>
  );
}

export default function AdminDashboard() {
  return (
    <DashboardProvider>
      <DashboardContent />
    </DashboardProvider>
  );
}